import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  IconArrowLeft,
  IconHistory,
  IconPlayerPlay,
  IconPlus,
  IconRestore,
  IconTrash,
} from '@tabler/icons-react';
import { useAppSelector } from '@shared/store';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { useAskPageContext } from '@shared/designVariants/useAskPageContext';
import { MarkdownEditor } from '@shared/components/MarkdownEditor';
import { ManageAgent } from '@pages/Admin/Main/Agent/ui/components/ManageAgent';
import { TestingAgentPopup } from '@widgets/Popup/TestingAgentPopup/TestingAgentPopup';
import OngoingCallsTab from '@pages/Admin/Campaign/ui/SidebarTabs/OngoingCallsTab';
import {
  TaskFlowPanel,
  type TaskFlowPanelHandle,
  type TaskFlowSaveState,
} from '@features/Admin/Agent/ui/TaskFlowCanvas';
import {
  useAgentProfileListControllerAgentProfileListForUsersQuery,
  useCampaignControllerCampaignQuery,
  useCampaignAgentListControllerListQuery,
  useCompanyControllerGetCompanyQuery,
  useUserMeControllerUserMeQuery,
  type AgentDto,
  type AgentProfileResponseDto,
  type CampaignDto,
  type CompanyDto,
} from '@shared/services/apiService';
import type { WorkspaceModule } from '@shared/designVariants/catalog';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { CampaignResources } from './CampaignResources';
import { AgentConfigurationChecks } from './AgentConfigurationChecks';
import { CopyJobAction } from './CopyJobAction';
import { WidgetAuthoring } from './WidgetAuthoring';
import { StudioPreCallSettings, preCallSettingsError } from './StudioPreCallSettings';
import { voicemailSupportReason } from './studioVoicemailSupport';
import { StudioPromptVariables } from './StudioPromptVariables';
import { StudioSessionSettings, sessionSettingsError } from './StudioSessionSettings';
import {
  StudioSpeechSettings,
  speechClarificationError,
  speechCampaignSupportError,
} from './StudioSpeechSettings';
import { useWorkspaceDraftGuard } from './useWorkspaceDraftGuard';
import { useWorkspaceScope } from './useWorkspaceScope';
import { useWorkspaceJobUsageQuery } from './workspaceApi';
import {
  useAgentStudioQuery,
  useAgentStudioVersionsQuery,
  useInitializeAgentStudioMutation,
  useCreateAgentStudioBranchMutation,
  useSaveAgentStudioBranchMutation,
  useDeleteAgentStudioBranchMutation,
  useSyncAgentStudioBranchMutation,
  useRestoreAgentStudioVersionMutation,
  usePublishAgentStudioBranchMutation,
  type StudioDetail,
  type StudioDraft,
  type StudioVersion,
} from './studioApi';

const profileValues = (profile: AgentProfileResponseDto) => ({
  name: profile.name,
  prompt: profile.prompt || '',
  firstMessage: profile.firstMessage,
  firstMessageInterruptionsEnabled: profile.firstMessageInterruptionsEnabled,
  taskFlow: profile.taskFlow,
});
const nativeResources: WorkspaceModule[] = [
  'Knowledge base',
  'Audiences',
  'Tools',
  'Webhooks',
  'Phone numbers',
  'WhatsApp',
  'Evaluations',
  'Analytics',
];

export const V4StudioPage = () => {
  const { campaignId = '', agentId = '' } = useParams();
  const scope = useWorkspaceScope();
  return (
    <StudioSession
      key={`${scope}:${campaignId}:${agentId}`}
      scope={scope}
      campaignId={campaignId}
      agentId={agentId}
    />
  );
};

const StudioSession = ({
  scope,
  campaignId,
  agentId,
}: {
  scope: string;
  campaignId: string;
  agentId: string;
}) => {
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const workspace = useDesignWorkspace();
  const target = { scope, campaignId, agentId };
  const query = useAgentStudioQuery(target, {
    refetchOnMountOrArgChange: true,
    pollingInterval: 15000,
    skipPollingIfUnfocused: true,
  });
  const campaignQuery = useCampaignControllerCampaignQuery({ id: campaignId });
  const agentsQuery = useCampaignAgentListControllerListQuery({ id: campaignId });
  const profilesQuery = useAgentProfileListControllerAgentProfileListForUsersQuery(
    { filter: undefined },
    { refetchOnMountOrArgChange: true },
  );
  const companyQuery = useCompanyControllerGetCompanyQuery(
    { id: user?.companyId || '' },
    { skip: !user?.companyId, refetchOnMountOrArgChange: true },
  );
  const meQuery = useUserMeControllerUserMeQuery();
  const [initialize, initializing] = useInitializeAgentStudioMutation();
  const [initialized, setInitialized] = useState<StudioDetail>();
  const [error, setError] = useState('');
  const active = useRef(true);
  const visible = useRef(!workspace.module);
  const permitted = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  visible.current = !workspace.module;
  const campaign = campaignQuery.currentData;
  const agents = campaign?.squad?.agents ?? agentsQuery.currentData;
  const agent = agents?.find((row) => row.id === agentId);
  const me = meQuery.currentData;
  const contextProfile =
    !profilesQuery.isError &&
    profilesQuery.currentData?.find((profile) => profile.id === agent?.profileId);
  useAskPageContext(
    campaign && campaign.companyId === user?.companyId && !campaignQuery.isError
      ? {
          page: 'Agent Studio',
          campaign: { id: campaign.id, title: campaign.title },
          ...(!agentsQuery.isError && agent && { agent: { id: agent.id, name: agent.name } }),
          ...(contextProfile &&
            me?.companyId === user?.companyId && {
              job: { id: contextProfile.id, name: contextProfile.name },
            }),
        }
      : null,
  );
  const canManage =
    !!user &&
    user.role !== 'viewer' &&
    !meQuery.isError &&
    !campaignQuery.isError &&
    !agentsQuery.isError &&
    me?.companyId === user.companyId &&
    !!me?.privileges.includes('agent.manage');
  permitted.current = canManage;
  const detail = query.currentData ?? initialized;
  const missing = query.isError && 'status' in query.error && query.error.status === 404;
  if (
    (!campaign && campaignQuery.isFetching) ||
    (!agents && agentsQuery.isFetching) ||
    (!me && meQuery.isFetching)
  )
    return <CircularProgress aria-label="Loading V4 agent studio" />;
  if (!campaign || !agent || campaign.companyId !== user?.companyId)
    return (
      <Alert severity="error">
        This agent or its current permissions are unavailable.{' '}
        <Button onClick={() => workspace.open('Agents')}>Back to agents</Button>
      </Alert>
    );
  if (!detail)
    return (
      <Stack gap={2}>
        <Typography component="h1" variant="h3">
          {agent.name} · Agent Studio
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {missing ? (
          <>
            <Typography>
              Create a versioned Studio from this agent’s saved native configuration. Draft changes
              remain separate until reviewed publication.
            </Typography>
            <Button
              variant="contained"
              disabled={!canManage || initializing.isLoading}
              onClick={async () => {
                if (!permitted.current || !visible.current) return;
                setError('');
                try {
                  const result = await initialize(target).unwrap();
                  if (active.current && visible.current && permitted.current)
                    setInitialized(result);
                } catch (e) {
                  if (active.current && visible.current)
                    setError(
                      getApiErrorMessage(
                        e,
                        'Unable to initialize Studio. Retry safely using the same agent.',
                      ),
                    );
                }
              }}>
              Initialize agent studio
            </Button>
          </>
        ) : query.isError ? (
          <Alert severity="error">
            Studio could not be loaded.{' '}
            <Button onClick={() => void query.refetch()}>Retry Studio</Button>
          </Alert>
        ) : (
          <CircularProgress aria-label="Loading Studio record" />
        )}
      </Stack>
    );
  return (
    <StudioEditor
      initial={detail}
      latest={query.currentData ?? detail}
      target={target}
      campaign={campaign}
      agent={agent}
      agents={agents || []}
      company={companyQuery.currentData}
      companyError={companyQuery.isError}
      profiles={profilesQuery.currentData}
      profilesError={profilesQuery.isError}
      canManage={canManage && !query.isError}
      canTest={
        !meQuery.isError &&
        !campaignQuery.isError &&
        !agentsQuery.isError &&
        user?.role !== 'viewer' &&
        !!me?.privileges.includes('call.test')
      }
      queryError={query.isError || meQuery.isError || campaignQuery.isError || agentsQuery.isError}
      reload={async () => {
        const [result, ...prerequisites] = await Promise.all([
          query.refetch(),
          campaignQuery.refetch(),
          agentsQuery.refetch(),
          profilesQuery.refetch(),
          meQuery.refetch(),
          companyQuery.refetch(),
        ]);
        if (result.error || !result.data || prerequisites.some((item) => item?.error))
          throw new Error('The current Studio could not be loaded. Your draft is retained.');
        return result.data;
      }}
      reloadProfiles={async (id) => {
        const result = await profilesQuery.refetch();
        if (result.error) throw new Error('The current Job library could not be loaded.');
        return result.data?.find((profile) => profile.id === id);
      }}
      reloadNative={() => {
        void campaignQuery.refetch();
        void agentsQuery.refetch();
        void profilesQuery.refetch();
      }}
    />
  );
};

type Target = { scope: string; campaignId: string; agentId: string };
type EditorProps = {
  initial: StudioDetail;
  latest: StudioDetail;
  target: Target;
  campaign: CampaignDto;
  agent: AgentDto;
  agents: AgentDto[];
  company?: CompanyDto;
  companyError: boolean;
  profiles?: AgentProfileResponseDto[];
  profilesError: boolean;
  canManage: boolean;
  canTest: boolean;
  queryError: boolean;
  reload: () => Promise<StudioDetail>;
  reloadNative: () => void;
  reloadProfiles: (id: string) => Promise<AgentProfileResponseDto | undefined>;
};
type Publication = Target & { revision: number; branchId: string; requestId: string };
const StudioEditor = ({
  initial,
  latest,
  target,
  campaign,
  agent,
  agents,
  company,
  companyError,
  profiles,
  profilesError,
  canManage,
  canTest,
  queryError,
  reload,
  reloadNative,
  reloadProfiles,
}: EditorProps) => {
  const workspace = useDesignWorkspace();
  const first =
    initial.branches.find((row) => row.id === initial.activeBranchId) ?? initial.branches[0];
  const [record, setRecord] = useState(initial);
  const [selected, setSelected] = useState(first.id);
  const [draft, setDraft] = useState<StudioDraft>(first.draft);
  const [panel, setPanel] = useState('prompt');
  const [resource, setResource] = useState<WorkspaceModule>('Knowledge base');
  const [epoch, setEpoch] = useState(0);
  const [agentDirty, setAgentDirty] = useState(false);
  const [agentEpoch, setAgentEpoch] = useState(0);
  const [flowDirty, setFlowDirty] = useState(false);
  const [resourceDirty, setResourceDirty] = useState(false);
  const [flowState, setFlowState] = useState<TaskFlowSaveState>({
    canSave: false,
    isSaving: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dialog, setDialog] = useState<
    | 'branch'
    | 'publish'
    | 'sync'
    | 'delete'
    | 'restore'
    | 'versions'
    | 'sections'
    | 'variables'
    | 'expanded'
    | null
  >(null);
  const [branchName, setBranchName] = useState('');
  const [restoreVersion, setRestoreVersion] = useState<StudioVersion>();
  const [before, setBefore] = useState<string>();
  const [previousVersions, setPreviousVersions] = useState<StudioVersion[]>([]);
  const [testing, setTesting] = useState(false);
  const [pendingPublication, setPendingPublication] = useState<Publication | null>(null);
  const flowRef = useRef<TaskFlowPanelHandle>(null);
  const promptRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  const active = useRef(true);
  const fence = useRef({ epoch, visible: !workspace.module, allowed: canManage });
  fence.current = { epoch, visible: !workspace.module, allowed: canManage };
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const resetEditors = useCallback(() => {
    const next = fence.current.epoch + 1;
    fence.current.epoch = next;
    setEpoch(next);
  }, []);
  const branch = record.branches.find((row) => row.id === selected) ?? record.branches[0];
  const changed = !isEqual(draft, branch.draft);
  const dirty = changed || agentDirty || flowDirty || resourceDirty;
  const voicemailIssue = (candidate: StudioDraft) =>
    voicemailSupportReason({
      campaignType: campaign.type,
      squad: !!campaign.squad || !!campaign.squadId,
      transcriberKind: company?.transcribers?.find(
        (row) => row.id === candidate.agent.transcriberId,
      )?.kind,
      llmProviderKey: company?.llmProviders?.find((row) => row.id === candidate.agent.llmProviderId)
        ?.key,
      hasWorkflow: candidate.job?.values.taskFlow != null,
      hasMcpTools: agent.tools?.some((tool) => tool.type === 'mcp'),
      muteUntilGreeting: candidate.agent.runtimeSettings?.speech?.muteUntilGreeting,
      turnDetection: campaign.turnHandling?.turnDetection,
      interruptionMode: campaign.turnHandling?.interruptionMode,
    });
  const runtimeIssue =
    speechCampaignSupportError(
      draft.agent.runtimeSettings,
      campaign.type === 'inbound',
      voicemailIssue(draft),
    ) ||
    speechClarificationError(draft.agent.runtimeSettings) ||
    sessionSettingsError(draft.agent.runtimeSettings) ||
    preCallSettingsError(draft.agent.runtimeSettings, campaign.type === 'inbound');
  const externalStale = latest.revision > record.revision;
  const nativeStale = (
    latest.revision >= record.revision ? latest : record
  ).staleBranchIds.includes(branch.id);
  const currentProfile = profiles?.find((row) => row.id === draft.job?.id);
  const jobType =
    currentProfile?.type ??
    (draft.job?.id === record.native.draft.job?.id ? record.native.jobType : undefined);
  const protectedJob = jobType !== 'custom';
  const locked = !canManage || busy || externalStale || !!pendingPublication;
  const jobLocked = locked || protectedJob || profilesError || !draft.job;
  const usage = useWorkspaceJobUsageQuery(
    { scope: target.scope },
    { refetchOnMountOrArgChange: true },
  );
  const count = !usage.isError
    ? usage.currentData?.find((row) => row.profileId === draft.job?.id)
    : undefined;
  const jobChanged = !isEqual(draft.job, record.native.draft.job);
  const versions = useAgentStudioVersionsQuery(
    { ...target, before },
    { refetchOnMountOrArgChange: true, skip: dialog !== 'versions' && !before },
  );
  const versionRows = [
    ...new Map(
      [...previousVersions, ...(versions.currentData?.items || [])].map((row) => [row.id, row]),
    ).values(),
  ];
  const [createBranch] = useCreateAgentStudioBranchMutation();
  const [saveBranch] = useSaveAgentStudioBranchMutation();
  const [removeBranch] = useDeleteAgentStudioBranchMutation();
  const [syncBranch] = useSyncAgentStudioBranchMutation();
  const [restore] = useRestoreAgentStudioVersionMutation();
  const [publish] = usePublishAgentStudioBranchMutation();
  const accept = (next: StudioDetail, branchId = selected) => {
    const nextBranch =
      next.branches.find((row) => row.id === branchId) ??
      next.branches.find((row) => row.id === next.activeBranchId) ??
      next.branches[0];
    setRecord(next);
    setSelected(nextBranch.id);
    setDraft(nextBranch.draft);
    setAgentDirty(false);
    setFlowDirty(false);
    setResourceDirty(false);
    resetEditors();
  };
  const discard = useCallback(() => {
    setDraft(branch.draft);
    setAgentDirty(false);
    setFlowDirty(false);
    setResourceDirty(false);
    resetEditors();
  }, [branch, resetEditors]);
  const guard = useWorkspaceDraftGuard(dirty, discard);
  const validJob =
    !draft.job || (!!draft.job.values.name?.trim() && draft.job.id === draft.agent.profileId);
  const publicationChanged =
    !record.activeVersionId ||
    record.activeBranchId !== branch.id ||
    !isEqual(draft, record.native.draft);
  const canTestPublished =
    canTest &&
    !!record.activeVersionId &&
    record.activeBranchId === branch.id &&
    !publicationChanged &&
    !dirty &&
    !nativeStale &&
    !externalStale;
  const canPublish =
    publicationChanged &&
    !runtimeIssue &&
    !companyError &&
    !profilesError &&
    !locked &&
    !dirty &&
    !nativeStale &&
    campaign.status === 'inactive' &&
    validJob &&
    (!jobChanged || !draft.job || !!count);
  const run = async (
    operation: () => Promise<StudioDetail>,
    message: string,
    branchId: string | ((value: StudioDetail) => string) = selected,
    recovery = false,
    preserveEditors = false,
    readOnly = false,
  ) => {
    const own = fence.current.epoch;
    if (
      busyRef.current ||
      (!readOnly && !fence.current.allowed) ||
      !fence.current.visible ||
      (!recovery && (externalStale || pendingPublication))
    )
      return false;
    busyRef.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await operation();
      if (
        !active.current ||
        fence.current.epoch !== own ||
        !fence.current.visible ||
        (!readOnly && !fence.current.allowed)
      )
        return false;
      const nextId = typeof branchId === 'function' ? branchId(result) : branchId;
      if (preserveEditors) {
        setRecord(result);
        setDraft(result.branches.find((row) => row.id === nextId)!.draft);
      } else accept(result, nextId);
      setNotice(message);
      setDialog(null);
      return true;
    } catch (e) {
      if (active.current && fence.current.epoch === own && fence.current.visible)
        setError(
          getApiErrorMessage(
            e,
            e instanceof Error
              ? e.message
              : 'Studio update failed. Refresh to inspect the latest saved revision.',
          ),
        );
      return false;
    } finally {
      busyRef.current = false;
      if (active.current) setBusy(false);
    }
  };
  const saveDraft = async (next: StudioDraft) => {
    const issue =
      speechCampaignSupportError(
        next.agent.runtimeSettings,
        campaign.type === 'inbound',
        voicemailIssue(next),
      ) ||
      speechClarificationError(next.agent.runtimeSettings) ||
      sessionSettingsError(next.agent.runtimeSettings) ||
      preCallSettingsError(next.agent.runtimeSettings, campaign.type === 'inbound');
    if (issue) {
      if (active.current && fence.current.visible) setError(issue);
      return false;
    }
    return run(
      () =>
        saveBranch({
          ...target,
          revision: record.revision,
          branchId: branch.id,
          draft: next,
        }).unwrap(),
      'Branch draft saved. Native agent and shared job remain unchanged.',
      selected,
      false,
      true,
    );
  };
  const refresh = () =>
    guard.request(() => {
      void run(reload, 'Latest Studio loaded.', selected, true, false, true);
    });
  const publishCurrent = async () => {
    if (!pendingPublication && !canPublish) return;
    const intent = pendingPublication ?? {
      ...target,
      revision: record.revision,
      branchId: branch.id,
      requestId: crypto.randomUUID(),
    };
    const own = fence.current.epoch;
    if (busyRef.current || !fence.current.allowed || !fence.current.visible) return;
    busyRef.current = true;
    setBusy(true);
    setPendingPublication(intent);
    setError('');
    try {
      const result = await publish(intent).unwrap();
      if (
        !active.current ||
        fence.current.epoch !== own ||
        !fence.current.visible ||
        !fence.current.allowed
      )
        return;
      setPendingPublication(null);
      accept(result.studio, intent.branchId);
      setDialog(null);
      setNotice(
        `Version ${result.version.number} published. Native agent and shared job were updated together.`,
      );
      setPreviousVersions([]);
      setBefore(undefined);
      reloadNative();
      void usage.refetch();
    } catch (e) {
      if (active.current && fence.current.epoch === own && fence.current.visible) {
        const code = typeof e === 'object' && e && 'status' in e ? e.status : undefined;
        if (typeof code === 'number' && code >= 400 && code < 500) setPendingPublication(null);
        setError(
          getApiErrorMessage(
            e,
            'Publication was not confirmed. Retry the same publication request to recover its result.',
          ),
        );
      }
    } finally {
      busyRef.current = false;
      if (active.current) setBusy(false);
    }
  };
  const draftAgent: AgentDto = {
    ...agent,
    ...draft.agent,
    // Native form edits agent catalogs only; staged runtime settings have their own editors.
    runtimeSettings: agent.runtimeSettings,
    idleState: draft.agent.idleState
      ? {
          ...draft.agent.idleState,
          customerSilenceMessages: draft.agent.idleState.customerSilenceMessages ?? [],
          customerEndCallMessages: draft.agent.idleState.customerEndCallMessages ?? [],
        }
      : undefined,
    profile: currentProfile ? { ...currentProfile, ...draft.job?.values } : null,
    voice: company?.voices?.find((row) => row.id === draft.agent.voiceId),
    transcriber: company?.transcribers?.find((row) => row.id === draft.agent.transcriberId),
    llmProvider: company?.llmProviders?.find((row) => row.id === draft.agent.llmProviderId),
  };
  const prompt = draft.job?.values.prompt || '';
  const sections = prompt
    .split('\n')
    .flatMap((line, index) =>
      /^#{1,6}\s+/.test(line) ? [{ title: line.replace(/^#{1,6}\s+/, ''), line: index }] : [],
    );
  const patchJob = (values: Partial<NonNullable<StudioDraft['job']>['values']>) => {
    if (!jobLocked && draft.job)
      setDraft({ ...draft, job: { ...draft.job, values: { ...draft.job.values, ...values } } });
  };
  const switchBranch = (id: string) => {
    if (busy || pendingPublication) return;
    guard.request(() => {
      const next = record.branches.find((row) => row.id === id);
      if (!next) return;
      setSelected(id);
      setDraft(next.draft);
      resetEditors();
      setError('');
    });
  };
  return (
    <Stack component="section" aria-label="Versioned V4 agent studio" gap={3}>
      <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Button startIcon={<IconArrowLeft size={16} />} onClick={() => workspace.open('Agents')}>
            Agents
          </Button>
          <Typography component="h1" variant="h4">
            {draft.agent.name}
          </Typography>
          <TextField
            select
            size="small"
            label="Branch"
            value={branch.id}
            disabled={busy || !!pendingPublication}
            onChange={(event) => switchBranch(event.target.value)}
            sx={{ minWidth: 160 }}>
            {record.branches.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          <Chip
            label={
              dirty
                ? 'Unsaved'
                : record.activeVersionId && record.activeBranchId === branch.id
                  ? 'Published branch'
                  : 'Draft branch'
            }
            size="small"
            variant="outlined"
          />
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button disabled={busy} onClick={refresh}>
            Refresh Studio
          </Button>
          <Button disabled={busy || !dirty} onClick={() => guard.request(() => {})}>
            Discard draft
          </Button>
          <Button
            variant="outlined"
            disabled={
              locked ||
              !!runtimeIssue ||
              (!changed && !flowDirty) ||
              resourceDirty ||
              (flowDirty && !flowState.canSave)
            }
            onClick={() => {
              if (flowDirty) void flowRef.current?.save();
              else void saveDraft(draft);
            }}>
            Save branch draft
          </Button>
          <Button
            startIcon={<IconPlayerPlay size={16} />}
            disabled={!canTestPublished || busy}
            onClick={() => setTesting(true)}>
            Test agent
          </Button>
          <Button startIcon={<IconHistory size={16} />} onClick={() => setDialog('versions')}>
            Version history
          </Button>
          <Button
            variant="contained"
            disabled={!canPublish && !pendingPublication}
            onClick={() => setDialog('publish')}>
            {pendingPublication ? 'Recover publication' : 'Review and publish'}
          </Button>
        </Stack>
      </Stack>
      {queryError && (
        <Alert severity="warning">
          The latest Studio or native prerequisites could not be loaded. Your current draft is
          retained; refresh before making changes.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      {!canManage && (
        <Alert severity="info">
          Current agent management permission is required to change this Studio.
        </Alert>
      )}
      {externalStale && (
        <Alert severity="warning">
          A newer Studio revision is saved on the server. Your draft is retained. Refresh to review
          it before writing.
        </Alert>
      )}
      {nativeStale && (
        <Alert
          severity="warning"
          action={
            <Button
              disabled={!canManage || busy || !!pendingPublication}
              onClick={() => setDialog('sync')}>
              Review sync
            </Button>
          }>
          Native agent or shared Job settings changed. Sync this branch before publishing.
        </Alert>
      )}
      {pendingPublication && (
        <Alert severity="warning">
          A publication outcome is awaiting confirmation. Recover the same request before editing or
          publishing another version.
        </Alert>
      )}
      {campaign.status !== 'inactive' && (
        <Alert severity="info">
          This campaign is active or finishing. Prepare branch drafts here; publishing requires an
          inactive campaign.
        </Alert>
      )}
      {(agentDirty || flowDirty) && (
        <Alert severity="info">
          Agent settings and workflow edits stay in their native editors until their draft Save
          action. Apply those edits before publication.
        </Alert>
      )}
      <Tabs
        value={panel}
        onChange={(_, next) => {
          if (resourceDirty) guard.request(() => setPanel(next));
          else setPanel(next);
        }}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="V4 Studio panels">
        {[
          ['prompt', 'Prompt'],
          ['branches', 'Branches'],
          ['configure', 'Configure & speech'],
          ['workflow', 'Workflow'],
          ['resources', 'Resources'],
          ['widget', 'Widget'],
          ['history', 'Conversations'],
        ].map(([value, label]) => (
          <Tab key={value} value={value} label={label} />
        ))}
      </Tabs>
      <Box
        sx={{
          display: panel === 'prompt' ? 'grid' : 'none',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 300px' },
          gap: 3,
        }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
          <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h5">Agent prompt</Typography>
              <Stack direction="row">
                <Button onClick={() => setDialog('variables')}>Variables</Button>
                <Button onClick={() => setDialog('sections')}>Sections ({sections.length})</Button>
                <Button onClick={() => setDialog('expanded')}>Expand prompt</Button>
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Instructions are edited in this branch. Publication applies the saved shared Job
              together with the agent choices.
            </Typography>
            {protectedJob && (
              <Alert severity="info">
                This Job is protected or its kind is unknown. Create an editable copy before
                changing instructions.
              </Alert>
            )}
            <Box ref={promptRef}>
              <MarkdownEditor
                value={prompt}
                onChange={(value) => patchJob({ prompt: value })}
                disabled={jobLocked}
                label="Agent instructions"
                height={560}
              />
            </Box>
            <TextField
              label="Job name"
              value={draft.job?.values.name || ''}
              disabled={jobLocked}
              onChange={(event) => patchJob({ name: event.target.value })}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              label="First message"
              value={draft.job?.values.firstMessage || ''}
              disabled={jobLocked}
              multiline
              minRows={3}
              onChange={(event) => patchJob({ firstMessage: event.target.value })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!draft.job?.values.firstMessageInterruptionsEnabled}
                  disabled={jobLocked}
                  onChange={(_, checked) => patchJob({ firstMessageInterruptionsEnabled: checked })}
                />
              }
              label="Allow interruptions during the opening message"
            />
          </Stack>
        </Paper>
        <Stack gap={2}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack gap={2}>
              <Typography variant="h6">Conversation settings</Typography>
              <Typography>Voice: {draftAgent.voice?.name || draft.agent.voiceId}</Typography>
              <Typography>Languages: {draft.agent.languages.join(', ')}</Typography>
              <Typography>
                Model: {draftAgent.llmProvider?.title || draft.agent.llmProviderId} ·{' '}
                {draft.agent.llmCustomModelKey || draft.agent.llmModelId}
              </Typography>
              <Typography>
                Transcriber: {draftAgent.transcriber?.provider || draft.agent.transcriberId}
              </Typography>
              <Button onClick={() => setPanel('configure')}>Configure agent choices</Button>
              <AgentConfigurationChecks campaign={campaign} agents={agents} />
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack gap={2}>
              <Typography variant="h6">Shared Job</Typography>
              <Typography>
                {count
                  ? `${count.agentCount} assistants across ${count.campaignCount} campaigns`
                  : 'Current usage is unknown.'}
              </Typography>
              {currentProfile && (
                <CopyJobAction
                  profile={currentProfile}
                  disabled={locked || dirty || profilesError}
                  loadCurrent={() => reloadProfiles(currentProfile.id)}
                  onCopied={(copy) => {
                    if (!active.current || !fence.current.visible || !fence.current.allowed) return;
                    setDraft({
                      ...draft,
                      agent: { ...draft.agent, profileId: copy.id },
                      job: { id: copy.id, values: profileValues(copy) },
                    });
                    void usage.refetch();
                  }}
                />
              )}
              <Button onClick={() => workspace.open('Jobs')}>Open Job library</Button>
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack gap={2}>
              <Typography variant="h6">Test the published configuration</Typography>
              <Typography variant="body2" color="text.secondary">
                Real tests use the saved native agent and Job. Branch drafts must be published
                before they can affect a test call.
              </Typography>
              <Button disabled={!canTestPublished} onClick={() => setTesting(true)}>
                Open test setup
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Box>
      {panel === 'branches' && (
        <Stack gap={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h5">Independent branch drafts</Typography>
            <Button
              startIcon={<IconPlus size={16} />}
              disabled={locked || dirty}
              onClick={() => {
                setBranchName('');
                setDialog('branch');
              }}>
              Create branch
            </Button>
          </Stack>
          {record.branches.map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography fontWeight={600}>{item.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.draft.agent.name} ·{' '}
                    {item.id === record.activeBranchId
                      ? 'Current published branch'
                      : 'Independent draft'}
                  </Typography>
                </Box>
                <Stack direction="row">
                  <Button onClick={() => switchBranch(item.id)}>Open {item.name}</Button>
                  <Button
                    startIcon={<IconTrash size={16} />}
                    color="error"
                    disabled={
                      locked || dirty || item.id !== selected || item.id === record.activeBranchId
                    }
                    onClick={() => setDialog('delete')}>
                    Delete {item.name}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
      <Box sx={{ display: panel === 'configure' ? 'block' : 'none' }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Agent choices · branch draft
          </Typography>
          <ManageAgent
            key={`agent:${branch.id}:${epoch}:${agentEpoch}`}
            presentation="panel"
            agents={agents}
            agent={draftAgent}
            campaignName={campaign.title}
            squadId={campaign.squad?.id}
            voiceData={company?.voices}
            transcriberData={company?.transcribers}
            llmProviders={company?.llmProviders}
            isCompanyLoading={!company}
            isCompanyError={companyError}
            blockedReason={locked ? 'Current Studio editing access is unavailable.' : undefined}
            onDirtyChange={setAgentDirty}
            onClose={() => guard.request(() => setPanel('prompt'))}
            onSaved={() => {
              setAgentDirty(false);
              setAgentEpoch((value) => value + 1);
            }}
            onSaveDraft={async (values) => {
              if (!fence.current.allowed || !fence.current.visible || locked) return false;
              if (
                values.profileId !== draft.agent.profileId &&
                (flowDirty || !isEqual(draft.job, branch.draft.job))
              ) {
                setError(
                  'Save or discard the current Job draft before assigning a different Job. Agent choices stay in their editor.',
                );
                return false;
              }
              const profile = profiles?.find((row) => row.id === values.profileId);
              if (values.profileId && !profile) {
                setError(
                  'The selected Job is unavailable in the current library. Refresh before saving.',
                );
                return false;
              }
              return saveDraft({
                agent: { ...draft.agent, ...values },
                job:
                  values.profileId === draft.job?.id
                    ? draft.job
                    : profile
                      ? { id: profile.id, values: profileValues(profile) }
                      : null,
              });
            }}
          />
          <Box sx={{ mt: 4 }}>
            <StudioSessionSettings
              value={draft.agent.runtimeSettings}
              disabled={locked}
              squad={!!campaign.squad || !!campaign.squadId}
              onChange={(runtimeSettings) => {
                if (locked) return;
                setDraft({ ...draft, agent: { ...draft.agent, runtimeSettings } });
              }}
            />
          </Box>
          <Box sx={{ mt: 4 }}>
            <StudioPreCallSettings
              value={draft.agent.runtimeSettings}
              disabled={locked}
              squad={!!campaign.squad || !!campaign.squadId}
              inbound={campaign.type === 'inbound'}
              onChange={(runtimeSettings) => {
                if (locked) return;
                setDraft({ ...draft, agent: { ...draft.agent, runtimeSettings } });
              }}
            />
          </Box>
          <Box sx={{ mt: 4 }}>
            <StudioSpeechSettings
              value={draft.agent.runtimeSettings}
              onChange={(runtimeSettings) => {
                if (locked) return;
                setDraft({ ...draft, agent: { ...draft.agent, runtimeSettings } });
              }}
              disabled={locked}
              voiceProvider={draftAgent.voice?.provider?.toLowerCase()}
              squad={!!campaign.squad || !!campaign.squadId}
              inbound={campaign.type === 'inbound'}
              languages={draft.agent.languages}
              voicemailUnavailableReason={voicemailIssue(draft)}
              maxEndpointingDelay={campaign.turnHandling?.maxEndpointingDelay}
            />
          </Box>
        </Paper>
      </Box>
      <Box sx={{ display: panel === 'workflow' ? 'block' : 'none' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Workflow Save persists this branch draft. Native agent and Job changes require
          publication.
        </Alert>
        <TaskFlowPanel
          key={`flow:${branch.id}:${epoch}`}
          ref={flowRef}
          initialTaskFlow={draft.job?.values.taskFlow}
          readOnly={jobLocked}
          profileName={draft.job?.values.name}
          onDirtyChange={setFlowDirty}
          onSaveStateChange={setFlowState}
          onSave={async (taskFlow) => {
            if (!draft.job || jobLocked) throw new Error('This Job is read only.');
            const saved = await saveDraft({
              ...draft,
              job: { ...draft.job, values: { ...draft.job.values, taskFlow } },
            });
            if (!saved) throw new Error('Branch draft was not saved.');
            return taskFlow;
          }}
        />
      </Box>
      {panel === 'resources' && (
        <Stack gap={2}>
          <Alert severity="info">
            These native campaign resources save directly in their existing scope. They are separate
            from branch draft publication.
          </Alert>
          <TextField
            select
            label="Campaign resource"
            value={resource}
            onChange={(event) =>
              guard.request(() => {
                setResource(event.target.value as WorkspaceModule);
                setResourceDirty(false);
              })
            }>
            {nativeResources.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
          <CampaignResources
            key={`${resource}:${epoch}`}
            module={resource}
            campaignId={campaign.id}
            onDirtyChange={setResourceDirty}
            requestNavigation={guard.request}
          />
        </Stack>
      )}
      {panel === 'widget' && (
        <WidgetAuthoring
          key={epoch}
          campaignId={campaign.id}
          agentId={agent.id}
          onDirtyChange={setResourceDirty}
        />
      )}
      {panel === 'history' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h5">Campaign conversations · {campaign.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
            Saved calls across all agents and handoffs in this campaign.
          </Typography>
          <Box sx={{ height: '70vh', minHeight: 500 }}>
            <OngoingCallsTab
              campaignId={campaign.id}
              campaignData={campaign}
              campaignType={campaign.type}
            />
          </Box>
        </Paper>
      )}
      <Dialog
        open={!!dialog}
        onClose={() => {
          if (!busy) setDialog(null);
        }}
        fullWidth
        maxWidth={dialog === 'expanded' ? 'xl' : 'md'}
        aria-labelledby="v4-studio-dialog-title">
        <DialogTitle id="v4-studio-dialog-title">
          {dialog === 'branch'
            ? 'Create independent branch'
            : dialog === 'publish'
              ? 'Review atomic publication'
              : dialog === 'sync'
                ? 'Sync branch with native configuration?'
                : dialog === 'delete'
                  ? 'Delete selected branch?'
                  : dialog === 'restore'
                    ? 'Restore version as draft?'
                    : dialog === 'versions'
                      ? 'Version history'
                      : dialog === 'sections'
                        ? 'Prompt sections'
                        : dialog === 'variables'
                          ? 'Prompt variables'
                          : 'Expanded agent prompt'}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            {dialog === 'branch' && (
              <TextField
                label="Branch name"
                value={branchName}
                disabled={busy}
                onChange={(event) => setBranchName(event.target.value)}
                inputProps={{ maxLength: 120 }}
              />
            )}
            {dialog === 'publish' && (
              <>
                <Alert severity="warning">
                  Publishing atomically applies this saved branch to the native agent and shared
                  Job. Active calls finish under their existing rules; new calls use the saved
                  configuration.
                </Alert>
                <Typography>
                  {branch.name} · {draft.agent.name}
                </Typography>
                <Typography>
                  {draft.job?.values.name || 'No shared Job'} ·{' '}
                  {count
                    ? `${count.agentCount} assistants across ${count.campaignCount} campaigns`
                    : 'Usage unknown'}
                </Typography>
                <Typography>
                  Style guidance:{' '}
                  {draft.agent.runtimeSettings?.session?.styleGuide === undefined
                    ? 'Native policy'
                    : draft.agent.runtimeSettings.session.styleGuide
                      ? 'On (Callevate standard)'
                      : 'Off'}
                </Typography>
                <Typography sx={{ overflowWrap: 'anywhere' }}>
                  Pre-call lookup:{' '}
                  {draft.agent.runtimeSettings?.preCall === undefined
                    ? 'Native policy (off)'
                    : draft.agent.runtimeSettings.preCall.enabled
                      ? `On · ${draft.agent.runtimeSettings.preCall.url}`
                      : 'Off'}
                </Typography>
                <Typography>
                  Phone voicemail screening:{' '}
                  {draft.agent.runtimeSettings?.speech?.voicemail === undefined
                    ? 'Native legacy policy'
                    : draft.agent.runtimeSettings.speech.voicemail
                      ? 'On (supported outbound phone calls only)'
                      : 'Off'}
                  . Browser/chat tests skip phone screening.
                </Typography>
                {jobChanged && draft.job && (
                  <Alert severity="warning">
                    Shared Job instructions or workflow changed. All assistants assigned to this Job
                    will use its newly saved values.
                  </Alert>
                )}
                {jobChanged && draft.job && !count && (
                  <Alert severity="warning">
                    Complete current usage is required before publishing changed shared
                    instructions.{' '}
                    <Button onClick={() => void usage.refetch()}>Refresh shared usage</Button>
                  </Alert>
                )}
                <Typography>
                  Languages: {draft.agent.languages.join(', ')} ·{' '}
                  {draft.job?.values.taskFlow?.tasks.length || 0} workflow tasks
                </Typography>
              </>
            )}
            {dialog === 'sync' && (
              <Alert severity="warning">
                Sync replaces this branch’s saved draft and your unapplied edits with the latest
                native agent and Job. Publication history is retained. Nothing is published by
                syncing.
              </Alert>
            )}
            {dialog === 'delete' && (
              <Alert severity="warning">
                Remove the independent branch “{branch.name}”. The server protects the current
                published branch and branches with publication history.
              </Alert>
            )}
            {dialog === 'restore' && (
              <>
                <Typography>
                  Restore version {restoreVersion?.number} into {branch.name}.
                </Typography>
                <Alert severity="warning">
                  This replaces the saved branch draft and unapplied edits. The native agent, shared
                  Job and current publication stay unchanged.
                </Alert>
              </>
            )}
            {dialog === 'versions' && (
              <>
                {versions.isError && (
                  <Alert severity="error">
                    Version history could not be loaded. Earlier rows remain available.{' '}
                    <Button onClick={() => void versions.refetch()}>Retry versions</Button>
                  </Alert>
                )}
                {versions.isFetching && (
                  <CircularProgress size={22} aria-label="Loading Studio versions" />
                )}
                {versionRows.map((version) => (
                  <Paper variant="outlined" key={version.id} sx={{ p: 2 }}>
                    <Stack gap={1}>
                      <Stack direction="row" gap={2} justifyContent="space-between">
                        <Typography fontWeight={600}>
                          Version {version.number} ·{' '}
                          {record.branches.find((row) => row.id === version.branchId)?.name ||
                            'Saved branch'}
                        </Typography>
                        {record.activeVersionId === version.id && (
                          <Chip label="Current publication" size="small" />
                        )}
                      </Stack>
                      <Typography variant="caption">
                        {new Date(version.createdAt).toLocaleString()}
                      </Typography>
                      <Typography>
                        {version.spec.agent.name} ·{' '}
                        {(version.spec.agent.languages || []).join(', ')}
                      </Typography>
                      <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {version.spec.job?.values.prompt || 'No Job instructions'}
                      </Typography>
                      <Button
                        startIcon={<IconRestore size={16} />}
                        disabled={locked}
                        onClick={() => {
                          setRestoreVersion(version);
                          setDialog('restore');
                        }}>
                        Restore version {version.number} as draft
                      </Button>
                    </Stack>
                  </Paper>
                ))}
                {!versions.isFetching && !versions.isError && !versionRows.length && (
                  <Typography>No published versions yet.</Typography>
                )}
                {versions.currentData?.next && (
                  <Button
                    disabled={versions.isFetching || versions.isError}
                    onClick={() => {
                      setPreviousVersions(versionRows);
                      setBefore(versions.currentData!.next!);
                    }}>
                    Load more versions
                  </Button>
                )}
              </>
            )}
            {dialog === 'variables' && (
              <StudioPromptVariables
                key={`${selected}:${draft.job?.id ?? 'none'}:${epoch}`}
                prompt={prompt}
                greeting={draft.job?.values.firstMessage || ''}
                agentName={draft.agent.name}
                agentId={agent.id}
                campaignId={campaign.id}
                readOnly={jobLocked}
                onInsert={(name) => patchJob({ prompt: `${prompt}\n{{${name}}}` })}
              />
            )}
            {dialog === 'sections' && (
              <>
                {sections.length ? (
                  sections.map((section) => (
                    <Button
                      key={section.line}
                      sx={{ justifyContent: 'start' }}
                      onClick={() => {
                        setDialog(null);
                        setPanel('prompt');
                        requestAnimationFrame(() => {
                          const textarea = promptRef.current?.querySelector('textarea');
                          if (!textarea) return;
                          const start =
                            prompt.split('\n').slice(0, section.line).join('\n').length +
                            (section.line ? 1 : 0);
                          textarea.focus();
                          textarea.setSelectionRange(start, start);
                          textarea.scrollTop = section.line * 22;
                        });
                      }}>
                      {section.title}
                    </Button>
                  ))
                ) : (
                  <Typography>Add Markdown headings to navigate prompt sections.</Typography>
                )}
              </>
            )}
            {dialog === 'expanded' && (
              <MarkdownEditor
                value={prompt}
                onChange={(value) => patchJob({ prompt: value })}
                disabled={jobLocked}
                height={650}
              />
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setDialog(null)}>
            {['branch', 'publish', 'sync', 'delete', 'restore'].includes(dialog || '')
              ? 'Cancel'
              : 'Close'}
          </Button>
          {dialog === 'branch' && (
            <Button
              variant="contained"
              disabled={
                locked ||
                dirty ||
                !branchName.trim() ||
                record.branches.some(
                  (row) => row.name.toLocaleLowerCase() === branchName.trim().toLocaleLowerCase(),
                )
              }
              onClick={() =>
                void run(
                  async () => {
                    const result = await createBranch({
                      ...target,
                      revision: record.revision,
                      name: branchName.trim(),
                      sourceBranchId: branch.id,
                    }).unwrap();
                    return result;
                  },
                  'Independent branch created.',
                  (result) =>
                    result.branches.find((row) => !record.branches.some((old) => old.id === row.id))
                      ?.id ?? selected,
                )
              }>
              Create branch draft
            </Button>
          )}
          {dialog === 'publish' && (
            <Button
              variant="contained"
              disabled={busy || !canManage || (!pendingPublication && !canPublish)}
              onClick={() => void publishCurrent()}>
              {pendingPublication ? 'Retry same publication' : 'Publish reviewed version'}
            </Button>
          )}
          {dialog === 'sync' && (
            <Button
              variant="contained"
              disabled={!canManage || busy || !!pendingPublication}
              onClick={() =>
                void run(
                  () =>
                    syncBranch({
                      ...target,
                      revision: record.revision,
                      branchId: branch.id,
                    }).unwrap(),
                  'Branch synced to current native configuration.',
                )
              }>
              Discard and sync branch
            </Button>
          )}
          {dialog === 'delete' && (
            <Button
              color="error"
              variant="contained"
              disabled={locked || dirty || branch.id === record.activeBranchId}
              onClick={() =>
                void run(
                  () =>
                    removeBranch({
                      ...target,
                      revision: record.revision,
                      branchId: branch.id,
                    }).unwrap(),
                  'Independent branch removed.',
                  record.activeBranchId,
                )
              }>
              Delete confirmed branch
            </Button>
          )}
          {dialog === 'restore' && (
            <Button
              variant="contained"
              disabled={locked || !restoreVersion}
              onClick={() =>
                restoreVersion &&
                void run(
                  () =>
                    restore({
                      ...target,
                      revision: record.revision,
                      branchId: branch.id,
                      versionId: restoreVersion.id,
                    }).unwrap(),
                  'Version restored as a branch draft. Publication is unchanged.',
                )
              }>
              Restore confirmed draft
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {testing && (
        <TestingAgentPopup
          open
          onClose={() => setTesting(false)}
          agentId={agent.id}
          agentName={agent.name}
          campaignId={campaign.id}
          companyHash={campaign.companyId}
          squadId={campaign.squad?.id}
        />
      )}
      {guard.dialog}
    </Stack>
  );
};
